import { useEffect, useRef, useState } from 'react';
import { billingApi } from './api';
import { logger } from '../lib/logger';

const MAX_POLLS = 20;
const POLL_INTERVAL_MS = 2000;

/**
 * Poll the backend for a Stripe Checkout Session's status.
 * Returns { state, tier } where state is one of:
 *   - 'checking' (still polling)
 *   - 'success'  (subscription provisioned)
 *   - 'timeout'  (max polls reached, session still open)
 *   - 'failed'   (session expired, or no sessionId, or repeated errors)
 *
 * Calls `onSuccess()` once when the subscription becomes complete.
 */
export function useBillingPolling(sessionId, onSuccess) {
  const [state, setState] = useState('checking');
  const [tier, setTier] = useState(null);
  const pollCount = useRef(0);
  const cancelled = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState('failed');
      return undefined;
    }
    cancelled.current = false;
    pollCount.current = 0;

    const pollOnce = async () => {
      if (cancelled.current) return;
      try {
        const res = await billingApi.checkoutStatus(sessionId);
        if (res.status === 'complete' && res.payment_status === 'complete') {
          setTier(res.tier);
          setState('success');
          if (typeof onSuccess === 'function') onSuccess();
          return;
        }
        if (res.status === 'expired') {
          setState('failed');
          return;
        }
      } catch (err) {
        // Network error — fall through to retry up to MAX_POLLS
        logger.debug('billing polling error (will retry):', err);
      }
      pollCount.current += 1;
      if (pollCount.current >= MAX_POLLS) {
        setState((current) => (current === 'checking' ? 'timeout' : current));
        return;
      }
      setTimeout(pollOnce, POLL_INTERVAL_MS);
    };

    pollOnce();
    return () => { cancelled.current = true; };
  }, [sessionId, onSuccess]);

  return { state, tier };
}
